/* eslint-disable react/prop-types */
/* eslint-disable no-underscore-dangle */
/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import {
  Avatar,
  Box,
  Checkbox,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  Button,
  InputAdornment,
  Stack,
  Tooltip,
  Select,
  MenuItem,
  Breadcrumbs,
  Link as MuiLink,
  ButtonGroup,
  Chip,
  Snackbar,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useAppDispatch, useAppSelector } from 'renderer/shared/state/hooks';
import { useListUsersQuery } from '../services/users.endpoints';
import { selectUsersTableQuery } from '../state/usersTable.selectors';
import {
  setUsersTableLimit,
  setUsersTablePageNumber,
  setUsersTableSearch,
  setUsersTableSortBy,
  setUsersTableSortOrder,
} from '../state/usersTable.slice';
import AddCustomerDialog from './AddCustomerDialog';

interface EnhancedTableToolbarProps {
  searchQuery: string;
  setSearch: (value: string) => void;
  onSearch: () => void;
  onReset: () => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  sortOrder: 'asc' | 'desc';
  setSortOrder: (value: 'asc' | 'desc') => void;
  setPage: (value: number) => void;
  onAddCustomer: () => void;
}

const EnhancedTableToolbar: React.FC<EnhancedTableToolbarProps> = ({
  searchQuery,
  setSearch,
  onSearch,
  onReset,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  setPage,
  onAddCustomer,
}) => {
  const handleSortChange = (event: any) => {
    setSortBy(event.target.value);
  };

  return (
    <Toolbar
      sx={{
        justifyContent: 'flex-start', // Align items to the left
        padding: '8px 0', // Remove horizontal padding to align search field to the left
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        backgroundColor: 'white',
      }}
    >
      <TextField
        variant="outlined"
        value={searchQuery}
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(1);
        }}
        placeholder="Search Users"
        size="small"
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'primary.main',
            },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <Button variant="outlined" onClick={onSearch} sx={{ mx: 1 }}>
        Search
      </Button>
      {searchQuery && (
        <Button variant="outlined" onClick={onReset}>
          Reset
        </Button>
      )}
      <Box sx={{ flexGrow: 1 }} />
      <Select
        labelId="filter-label"
        id="filter"
        value={sortBy}
        onChange={handleSortChange}
        sx={{
          minWidth: 120,
          '& .MuiSelect-select': {
            padding: '8px',
            borderRadius: '4px',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          marginRight: '16px',
        }}
      >
        <MenuItem value="firstName">First Name</MenuItem>
        <MenuItem value="lastName">Last Name</MenuItem>
        <MenuItem value="email">Email</MenuItem>
        <MenuItem value="createdAt">Created Date</MenuItem>
        <MenuItem value="lastLogin">Last Login</MenuItem>
        <MenuItem value="isEmailVerified">Verified</MenuItem>
        <MenuItem value="role">Role</MenuItem>
        <MenuItem value="status">Status</MenuItem>
      </Select>
      <ButtonGroup sx={{ marginRight: 4 }}>
        <Button
          variant={sortOrder === 'asc' ? 'contained' : 'outlined'}
          onClick={() => setSortOrder('asc')}
          size="small"
        >
          Asc
        </Button>
        <Button
          variant={sortOrder === 'desc' ? 'contained' : 'outlined'}
          onClick={() => setSortOrder('desc')}
          size="small"
        >
          Desc
        </Button>
      </ButtonGroup>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={onAddCustomer}
        sx={{
          boxShadow: 'none',
          textTransform: 'none',
          borderRadius: '4px',
          padding: '6px 16px',
        }}
      >
        Add Customer
      </Button>
    </Toolbar>
  );
};

const UserList = () => {
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [feedbackToast, setFeedbackToast] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);
  const { page, limit, sortBy, sortOrder, search } = useAppSelector(
    selectUsersTableQuery,
  );
  const dispatch = useAppDispatch();
  const setPage = (p: number) => dispatch(setUsersTablePageNumber(p));
  const setLimit = (l: number) => dispatch(setUsersTableLimit(l));
  const setSortBy = (s: string) => dispatch(setUsersTableSortBy(s));
  const setSortOrder = (s: 'asc' | 'desc') =>
    dispatch(setUsersTableSortOrder(s));
  const setSearch = (s: string) => dispatch(setUsersTableSearch(s));
  const {
    data: users,
    isLoading,
    isError,
    error,
  } = useListUsersQuery({
    page,
    limit,
    search,
    sortBy,
    sortOrder,
  });

  const handleSearch = () => {};

  const handleReset = () => {
    setSearch('');
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (isError) {
    if ('status' in error) {
      return <div>Error: {error.status}</div>;
    }
    return <div>An error occurred</div>;
  }

  if (users === undefined) {
    return <div>Loading...</div>;
  }

  const getInitials = (firstName: string, lastName: string) => {
    const firstInitial = firstName ? firstName[0].toUpperCase() : '';
    const lastInitial = lastName ? lastName[0].toUpperCase() : '';
    return `${firstInitial}${lastInitial}`;
  };

  const handleChangeRowsPerPage = (event: { target: { value: string } }) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1);
  };

  const formatDateAndTime = (dateString: string) => {
    const date = moment(dateString);
    return {
      formattedDate: date.format('MMM Do YYYY'),
      formattedTime: date.format('h:mm A'),
    };
  };

  const statusColors: Record<string, string> = {
    active: 'green',
    inactive: 'gray',
    suspended: 'orange',
    deleted: 'red',
  };
  const getStatusColor = (status: keyof typeof statusColors) => {
    return statusColors[status] || 'default';
  };

  const avatarStyle = () => ({
    bgcolor: '#0426ae',
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.12)',
  });

  const capitalize = (str: string) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };
  const VerificationChip = ({ isVerified }: { isVerified: boolean }) => (
    <Chip
      label={isVerified ? 'Yes' : 'No'}
      color={isVerified ? 'success' : 'default'}
      size="small"
      variant="outlined"
    />
  );

  const StatusChip = ({ status }: { status: string }) => (
    <Chip
      label={capitalize(status)}
      size="small"
      style={{
        backgroundColor: getStatusColor(status),
        color: 'white',
      }}
    />
  );

  const actionButtonColors = {
    // view: 'info', // Blue
    // edit: 'primary', // Theme primary color
    // delete: 'error', // Red

    view: '#17a2b8', // Blue
    edit: '#1976d2', // Theme primary color
    delete: '#dc3545', // Red
  };

  return (
    <Box>
      <Snackbar
        open={Boolean(feedbackToast)}
        autoHideDuration={8000}
        onClose={() => setFeedbackToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={feedbackToast?.severity ?? 'success'}
          variant="filled"
          onClose={() => setFeedbackToast(null)}
          sx={{ width: '100%' }}
        >
          {feedbackToast?.message}
        </Alert>
      </Snackbar>

      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
        <MuiLink underline="hover" color="inherit" href="/#">
          Home
        </MuiLink>
        <Typography color="text.primary">Users</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Users
      </Typography>

      <Box sx={{ width: '100%' }}>
        <Paper sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
          <EnhancedTableToolbar
            sortBy={sortBy}
            setSortBy={setSortBy}
            sortOrder={sortOrder as 'asc' | 'desc'}
            setSortOrder={setSortOrder}
            searchQuery={search}
            setSearch={(s) => setSearch(s)}
            onSearch={handleSearch}
            onReset={handleReset}
            setPage={setPage}
            onAddCustomer={() => setAddCustomerOpen(true)}
          />
          <AddCustomerDialog
            open={addCustomerOpen}
            onClose={() => setAddCustomerOpen(false)}
            onFeedback={setFeedbackToast}
          />
          <TableContainer>
            <Table
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: 'rgba(224, 224, 224, 1)',
                  color: 'black',
                  fontWeight: 'bold',
                },
                '& .MuiTableCell-body': {
                  fontSize: '0.875rem',
                  '& .MuiAvatar-root': {
                    verticalAlign: 'middle',
                    marginRight: '8px',
                  },
                },
                '& .MuiIconButton-root': {
                  padding: '8px',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.07)',
                },
              }}
              aria-labelledby="tableTitle"
            >
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox />
                  </TableCell>
                  <TableCell>User Info</TableCell>
                  <TableCell>Verified</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.docs.map((user) => {
                  return (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={user._id}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox />
                      </TableCell>
                      <TableCell component="th" scope="row" padding="none">
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Avatar sx={avatarStyle()}>
                            {getInitials(user.firstName, user.lastName)}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" noWrap>
                              {`${user.firstName} ${user.lastName}`}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              noWrap
                              sx={{ fontSize: '0.8rem' }}
                            >
                              {user.email}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <VerificationChip isVerified={user.isEmailVerified} />
                      </TableCell>
                      <TableCell>
                        {user.lastLogin ? (
                          <Box>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              component="span"
                            >
                              {formatDateAndTime(user.lastLogin).formattedDate}
                            </Typography>
                            <br />
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              component="span"
                            >
                              {formatDateAndTime(user.lastLogin).formattedTime}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            -
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            component="span"
                          >
                            {formatDateAndTime(user.createdAt).formattedDate}
                          </Typography>
                          <br />
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            component="span"
                          >
                            {formatDateAndTime(user.createdAt).formattedTime}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {user.role === 'admin' ? (
                          <Tooltip title="Admin" placement="top">
                            <AdminPanelSettingsIcon
                              style={{ color: '#d32f2f' }}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title="User" placement="top">
                            <PersonIcon style={{ color: '#1976d2' }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <StatusChip status={user.status} />
                      </TableCell>
                      <TableCell align="right">
                        <Link to={`/users/${user._id}`}>
                          <Tooltip title="View">
                            <IconButton sx={{ color: actionButtonColors.view }}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={users.totalDocs}
            page={page - 1}
            onPageChange={(e, pageNumber) => setPage(pageNumber + 1)}
            rowsPerPage={limit}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25]}
            sx={{
              '.MuiTablePagination-toolbar': {
                minHeight: '52px',
                paddingRight: '32px',
              },
              '.MuiTablePagination-selectLabel': {
                color: 'rgba(0, 0, 0, 0.54)',
                lineHeight: '1.4375em',
                marginRight: '8px',
              },
              '.MuiTablePagination-select': {
                marginRight: '32px',
              },
              '.MuiTablePagination-displayedRows': {
                flex: 'none',
                marginRight: 'auto',
              },
              '.MuiTablePagination-actions': {
                marginLeft: 0,
                '& .MuiIconButton-root': {
                  padding: '8px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                },
                '& .Mui-disabled': {
                  opacity: 0.3,
                },
              },
              '.MuiTablePagination-actions > button': {
                color: 'rgba(0, 0, 0, 0.54)',
              },
            }}
          />
        </Paper>
      </Box>
    </Box>
  );
};

const Users = () => {
  return (
    <Stack spacing={1}>
      <UserList />
    </Stack>
  );
};

export default Users;
